import {
  Children,
  forwardRef,
  isValidElement,
  useEffect,
  useImperativeHandle,
  useRef,
  type ReactElement,
  type ReactNode,
} from "react";
import { PageFlip } from "page-flip";
// page-flip ships its CSS only under src/Style/ — the package's dist/ is
// JS-only. Without these rules .stf__parent / .stf__block / .stf__item
// have no position or perspective, the canvas is 0×0, and pages render
// invisibly.
import "page-flip/src/Style/stPageFlip.css";

export type FlipBookHandle = {
  flipNext: () => void;
  flipPrev: () => void;
  getCurrentPageIndex: () => number;
  getPageCount: () => number;
};

type FlipBookProps = {
  /** One <Page> element per page. The first and last children are treated
   *  as cover/back via the engine's showCover option and should pass
   *  density="hard". */
  children: ReactNode;
  /** When true, the engine renders one page at a time (mobile). When false,
   *  it renders a two-page spread (desktop). The parent decides via its
   *  own breakpoint and remounts the FlipBook with a different `key` so
   *  the engine starts fresh on each orientation change. */
  usePortrait: boolean;
  /** Fired after every successful flip with the new page index. */
  onFlip?: (pageIndex: number) => void;
  /** Fired once after the engine reports it has initialised, with the
   *  total page count. Useful for driving "N / total" counters. */
  onInit?: (pageCount: number) => void;
  /** Page index the engine opens on (default 0 = cover/first spread). */
  startPage?: number;
};

/**
 * Thin React wrapper around the page-flip (StPageFlip) engine.
 *
 * We don't depend on react-pageflip; this is our adapter. The engine takes
 * over the child page DOM nodes via loadFromHTML and renders them inside
 * its own canvas-driven layout. Because of that, the children should be
 * stable for the lifetime of the component — change `key` to rebuild.
 */
const FlipBook = forwardRef<FlipBookHandle, FlipBookProps>(function FlipBook(
  { children, usePortrait, onFlip, onInit, startPage = 0 },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Array<HTMLElement | null>>([]);
  const pageFlipRef = useRef<PageFlip | null>(null);
  // StrictMode in development runs every effect twice (setup → cleanup →
  // setup) against the same component instance. The page-flip engine's
  // loadFromHTML re-parents our page <div>s into its own DOM, and destroy()
  // removes that DOM — taking our page nodes with it. The second setup
  // would then operate on detached 0×0 nodes.
  //
  // Fix: defer destroy to a microtask-after-paint via setTimeout. If
  // setup runs again synchronously (StrictMode), we cancel the pending
  // destroy and skip re-init — the existing engine keeps running. On a
  // real unmount the timer fires and destroys cleanly. A true remount
  // (parent's `key` change) instantiates a fresh component with a fresh
  // destroyTimeoutRef so this gate doesn't block legitimate re-inits.
  const destroyTimeoutRef = useRef<number | null>(null);

  useImperativeHandle(ref, () => ({
    flipNext: () => pageFlipRef.current?.flipNext(),
    flipPrev: () => pageFlipRef.current?.flipPrev(),
    getCurrentPageIndex: () =>
      pageFlipRef.current?.getCurrentPageIndex() ?? 0,
    getPageCount: () => pageFlipRef.current?.getPageCount() ?? 0,
  }));

  useEffect(() => {
    if (destroyTimeoutRef.current !== null) {
      // StrictMode second pass: a destroy is pending from the synthetic
      // cleanup. Cancel it and keep the existing engine alive.
      window.clearTimeout(destroyTimeoutRef.current);
      destroyTimeoutRef.current = null;
      return;
    }
    if (!containerRef.current) return;
    const elements = pageRefs.current.filter(
      (el): el is HTMLElement => el != null,
    );
    if (elements.length === 0) return;

    // Square image area (600×600 — book illustrations are 1:1 by default)
    // plus a fixed 128-px strip below for narrative + page number, so the
    // image fills its placeholder without letterboxing and text has its own
    // readable space beneath it. Total page = 600 × 728.
    const pf = new PageFlip(containerRef.current, {
      width: 600,
      height: 728,
      size: "stretch",
      minWidth: 280,
      maxWidth: 600,
      minHeight: 340,
      maxHeight: 728,
      // Cover/back-alone makes sense in portrait (single page at a time)
      // but in landscape we want [dedication, cover] paired as the first
      // spread, like the inside-front-cover + title-page of a real
      // hardcover book. Reader.tsx already builds slot arrays matching
      // this contract per orientation.
      showCover: usePortrait,
      usePortrait,
      flippingTime: 700,
      maxShadowOpacity: 0.5,
      drawShadow: true,
      showPageCorners: true,
      useMouseEvents: true,
      clickEventForward: true,
      mobileScrollSupport: true,
      // Block tap-anywhere-to-flip. In portrait the engine's `getDirectionByPoint`
      // would otherwise interpret a tap on the left half of the single visible
      // page as "go back" and play a slide-in-from-left animation that doesn't
      // look like a page flip (the engine reuses the previous page as the
      // animating element and skips the bottom-page draw — see
      // node_modules/page-flip/src/Render/HTMLRender.ts L317). Disabling
      // click-to-flip removes that path entirely; users still flip via the
      // ‹ Prev / Next › buttons, swipes, and corner-zone clicks.
      disableFlipByClick: true,
      autoSize: true,
      startPage,
    });

    pf.loadFromHTML(elements);
    pageFlipRef.current = pf;
    narrowCornerHitZone(pf);
    letProgrammaticFlipsBypassClickGate(pf);

    onInit?.(pf.getPageCount());

    pf.on("flip", (e: unknown) => {
      const idx = (e as { data?: number }).data ?? 0;
      onFlip?.(idx);
    });

    return () => {
      // Defer destroy. If we're in StrictMode's synthetic unmount cycle,
      // the next setup will fire synchronously and clear this timer
      // before it fires, keeping the engine alive. On a real unmount the
      // timer fires and destroys cleanly.
      destroyTimeoutRef.current = window.setTimeout(() => {
        pageFlipRef.current?.destroy();
        pageFlipRef.current = null;
        destroyTimeoutRef.current = null;
      }, 50);
    };
    // We deliberately mount once per `key`-driven lifetime; orientation
    // changes are handled by the parent remounting us. Including the
    // callback props in deps would tear down the engine on every parent
    // render, which is what we want to avoid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wrap each child in a ref-capturing div so we can hand the DOM nodes
  // to the engine. The engine then re-parents these nodes — React stops
  // managing them after the first render.
  pageRefs.current = [];
  const wrapped = Children.map(children, (child, idx) => {
    if (!isValidElement(child)) return null;
    return (
      <div
        ref={(el) => {
          pageRefs.current[idx] = el;
        }}
      >
        {child as ReactElement}
      </div>
    );
  });

  return (
    <div ref={containerRef} className="tp-flipbook">
      {wrapped}
    </div>
  );
});

FlipBook.displayName = "FlipBook";

/**
 * The engine hardcodes its corner-flip hit zone as `diagonal / 5`, which
 * makes ~28% of each page sensitive to corner clicks/hovers — far too
 * easy to trigger accidentally. There is no setting to configure this in
 * page-flip 2.0.7, so we replace `isPointOnCorners` on the engine's
 * internal `flipController` with a tighter version. The flipController
 * is created inside `loadFromHTML`, so this must run after that call.
 */
function narrowCornerHitZone(pf: PageFlip): void {
  type Pt = { x: number; y: number };
  type Rect = { pageWidth: number; width: number; height: number };
  type FlipPrivate = {
    getBoundsRect: () => Rect;
    render: { convertToBook: (p: Pt) => Pt };
    isPointOnCorners: (p: Pt) => boolean;
  };
  const flipController = (pf as unknown as { flipController?: FlipPrivate })
    .flipController;
  if (!flipController) return;
  flipController.isPointOnCorners = function (globalPos: Pt): boolean {
    const rect = this.getBoundsRect();
    // Diagonal / 10 ≈ 14% of page — narrow enough to be a clear corner
    // but big enough to hit comfortably on touch.
    const operatingDistance =
      Math.sqrt(rect.pageWidth ** 2 + rect.height ** 2) / 10;
    const bookPos = this.render.convertToBook(globalPos);
    return (
      bookPos.x > 0 &&
      bookPos.y > 0 &&
      bookPos.x < rect.width &&
      bookPos.y < rect.height &&
      (bookPos.x < operatingDistance ||
        bookPos.x > rect.width - operatingDistance) &&
      (bookPos.y < operatingDistance ||
        bookPos.y > rect.height - operatingDistance)
    );
  };
}

/**
 * The engine routes its own `flipPrev` / `flipNext` (called by our buttons
 * and by the engine's swipe-end handler) through the same gated `flip()`
 * method that `disableFlipByClick` guards — with hardcoded coordinates that
 * don't necessarily land on a corner per our narrowed hit-zone. In portrait
 * specifically, `flipPrev`'s `{x: 10, ...}` lands in the book's left half
 * (off-screen virtual page), so the corner-gate would reject it and the
 * Prev button + swipe-right would silently no-op. Wrap both calls to flip
 * the setting off for the duration of the programmatic flip.
 */
function letProgrammaticFlipsBypassClickGate(pf: PageFlip): void {
  type Corner = "top" | "bottom";
  type Controller = {
    flipPrev: (corner: Corner) => void;
    flipNext: (corner: Corner) => void;
  };
  const controller = (pf as unknown as { flipController?: Controller })
    .flipController;
  if (!controller) return;
  const setting = (pf as unknown as { setting: { disableFlipByClick: boolean } })
    .setting;
  const origFlipPrev = controller.flipPrev.bind(controller);
  const origFlipNext = controller.flipNext.bind(controller);
  const bypass = (run: () => void) => {
    const prev = setting.disableFlipByClick;
    setting.disableFlipByClick = false;
    try {
      run();
    } finally {
      setting.disableFlipByClick = prev;
    }
  };
  controller.flipPrev = (corner) => bypass(() => origFlipPrev(corner));
  controller.flipNext = (corner) => bypass(() => origFlipNext(corner));
}

export default FlipBook;
